import { z } from 'zod';
import { PRIVATE_MATCH_ROLES, PRIVATE_MATCH_STATUSES, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, } from '../constants/index.js';
import { API_ERROR_CODES } from '../types/api.js';
import { characterSchema } from './character.js';
import { profileSchema } from './profile.js';
/**
 * プライベートマッチ（#153、[ADR-024](../../../../docs/adr/024-private-match-rooms.md)）。
 *
 * 型ではなく **zod スキーマが仕様の正**（[ADR-005](../../../../docs/adr/005-zod-in-shared-package.md)）。
 * iOS も同じ形を読むので、変えるときは `packages/contracts/mobile/` の JSON も合わせる。
 */
const ROOM_CODE_PATTERN = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
/**
 * 人が入力したルームコードを、比べられる形にそろえる。
 *
 * **大文字小文字を区別しない**（小文字で打たれても通す）。読み上げたときに区切ったハイフンや空白も落とす。
 * 入力欄の都合はここで吸収し、D1 には正規化した形だけを入れる。
 */
export function normalizeRoomCode(input) {
    return input.replace(/[\s-]/g, '').toUpperCase();
}
/** 正規化済みのルームコード。レスポンスと D1 の値はこの形。 */
export const roomCodeSchema = z.string().regex(ROOM_CODE_PATTERN);
/**
 * 人が入力したルームコード（リクエスト）。正規化してから形を確かめる。
 *
 * 読み違えやすい文字（`0` `O` `1` `I` `L`）はコードに現れないので、打たれたら 400 にする
 * （アプリは入力欄でそもそも打てないようにする）。
 */
export const roomCodeInputSchema = z
    .string()
    .transform(normalizeRoomCode)
    .pipe(z.string().regex(ROOM_CODE_PATTERN, 'ルームコードは 6 文字です'));
export const privateMatchStatusSchema = z.enum(PRIVATE_MATCH_STATUSES);
export const privateMatchRoleSchema = z.enum(PRIVATE_MATCH_ROLES);
/**
 * 相手（#164）。**相手の選手は、対戦が成立する（`MATCHED`）まで返さない。**
 *
 * 対戦の前に相手の選手（絵・名前・ステータス）が見えると、ゲストは相性のよい選手を後出しで選べる。
 * 画面で隠すだけではアプリを介さずに読めるので、サーバーが返さない（企画書 §40-7）。
 * 取りやめ・期限切れになったルームでは最後まで返さない。
 *
 * **利用者のプロフィール（ニックネームとアバター）は、相手が来た時点から見せる**（#169、ADR-025）。
 * 選手とは別のもので、後出しに使えない。
 */
export const privateMatchOpponentSchema = z.object({
    /**
     * 相手の利用者の ID（#277）。**相手がいれば、対戦の前からある。** ロビーで相手のニックネームとアバターを通報する・
     * みえなくする（#264）ときに送る。相手の選手の `ownerId` と同じ値。
     */
    userId: z.string().min(1),
    /** 相手の選手。`MATCHED` のときだけある。 */
    character: characterSchema.optional(),
    /**
     * 相手のニックネームとアバター（#169）。**相手がいれば、対戦の前からある。**
     *
     * ニックネームが空なら、アプリは「あいて」と出す。
     */
    profile: profileSchema,
});
/**
 * ルーム（`POST /private-matches` などのレスポンス）。**ホストとゲストにしか返さない。**
 *
 * **受け取る人から見た形**（`myCharacter` / `opponent`）。相手の選手は成立するまで無い（`privateMatchOpponentSchema`）。
 * 相手のニックネームとアバターは、相手が来た時点からある（#169）。
 */
export const privateMatchSchema = z.object({
    id: z.string().min(1),
    code: roomCodeSchema,
    /** 期限切れは時刻から導いた値（`PRIVATE_MATCH_STATUSES` のコメント）。 */
    status: privateMatchStatusSchema,
    /** リクエストした人の立場。**対戦ではホストが A、ゲストが B。** */
    role: privateMatchRoleSchema,
    /**
     * 招待リンク（`https://<サイト>/m/<code>`）。QR コードの中身もこれ。
     *
     * **サーバーが組み立てる。** 環境ごとにサイトのドメインが違い（staging / 本番）、アプリが持つと
     * staging のルームのリンクが本番のドメインを指す事故が起きる。
     */
    inviteUrl: z.string().url(),
    /** 自分の選手。 */
    myCharacter: characterSchema,
    /** 相手。ホストから見て、ゲストが参加するまで無い。 */
    opponent: privateMatchOpponentSchema.optional(),
    /**
     * 成立した対戦。`MATCHED` のときだけある。**リプレイは `GET /battles/:battleId` で引く。**
     *
     * ロビーの WebSocket を取りこぼしても、`GET /private-matches/:id` でこれを受け取れる。
     */
    battleId: z.string().min(1).optional(),
    expiresAt: z.string().min(1),
    createdAt: z.string().min(1),
});
/**
 * 参加する前の確認（`GET /private-matches/by-code/:code`）。
 *
 * コードが参加できるルームを指しているかを確かめる。参加できないルーム（期限切れ・満員・締め切られた・
 * 自分のルーム）はここでエラーにするので、状態は持たない。**ホストの選手は返さない**（#164。
 * 見てから選手を選べると平等でない。`privateMatchOpponentSchema`）。
 */
export const privateMatchPreviewSchema = z.object({
    code: roomCodeSchema,
    expiresAt: z.string().min(1),
});
/** ルームを作る（`POST /private-matches`）。**選手の ID だけ**を受ける（企画書 §40-7）。 */
export const createPrivateMatchSchema = z.object({
    characterId: z.string().min(1),
});
/** ルームに参加する（`POST /private-matches/join`）。 */
export const joinPrivateMatchSchema = z.object({
    code: roomCodeInputSchema,
    characterId: z.string().min(1),
});
/** `GET /private-matches/by-code/:code` のパス。 */
export const privateMatchCodeParamSchema = z.object({
    code: roomCodeInputSchema,
});
// ---------------------------------------------------------------------------
// ロビー（WebSocket）のメッセージ
// ---------------------------------------------------------------------------
//
// **`type` で区別する。** アプリは知らない `type` を無視すること（古いアプリが新しいサーバーに繋がる状況は
// 必ず起きる）。サーバーは受け取ったメッセージをこのスキーマで検証してから扱い、知らない `type` にはエラーを返す。
/** 片方のプレイヤーのロビーでの様子。 */
export const lobbyPresenceSchema = z.object({
    /** 今 WebSocket がつながっているか。アプリがバックグラウンドに回ると切れる。 */
    connected: z.boolean(),
    /** 準備完了を送ったか。**切れても消えない**（戻ってくる前に相手が準備完了すれば、そのまま対戦が始まる）。 */
    ready: z.boolean(),
});
/**
 * アプリ → サーバー。
 *
 * - `ready` — 準備完了（`ready: false` で取り消す）。両者がそろい、ルームが `FULL` なら対戦が始まる
 * - `ping` — 生存確認。ふつうは `LOBBY_PING` と同じ文字列で送り、DO を起こさずに返させる
 */
export const lobbyClientMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ready'), ready: z.boolean() }),
    z.object({ type: z.literal('ping') }),
]);
/**
 * サーバー → アプリ：ルームの状態を**丸ごと**。接続したときと、何か変わるたびに送る（差分は送らない）。
 *
 * `room` は `GET /private-matches/:id` と同じ形で、受け取る人の立場（`role`）で作る。`MATCHED` になったら
 * `room.battleId` と相手の選手（`room.opponent.character`）が入り、サーバーは接続を閉じる。
 */
export const lobbyStateMessageSchema = z.object({
    type: z.literal('state'),
    room: privateMatchSchema,
    presence: z.object({
        host: lobbyPresenceSchema,
        /** ゲストが参加する前は、つながっておらず準備もしていない。 */
        guest: lobbyPresenceSchema,
    }),
});
/** サーバー → アプリ：受け取ったメッセージを扱えなかった。接続は閉じない。 */
export const lobbyErrorMessageSchema = z.object({
    type: z.literal('error'),
    error: z.object({
        code: z.enum(API_ERROR_CODES),
        message: z.string().min(1),
    }),
});
/** サーバー → アプリ：`ping` への返事。 */
export const lobbyPongMessageSchema = z.object({ type: z.literal('pong') });
export const lobbyServerMessageSchema = z.discriminatedUnion('type', [
    lobbyStateMessageSchema,
    lobbyErrorMessageSchema,
    lobbyPongMessageSchema,
]);
